import { type ReactNode, useMemo } from "react";

import { onboarding } from "byzantine";

import { isRouteInDashboard } from "../../../components/dashboard/useIsDashboardDialogOpen";
import {
  useIncomeReportingCard,
  IncomeReportingContainer,
} from "../incomeReporting";

import type {
  OnboardingPrompt,
  OnboardingEventCallbackMap,
} from "byzantine/src/dbbl/businessLogic/modules/onboarding";

const { OnboardingEventName } = onboarding;

/**
 * Routes to a URL, using SPA navigation for dashboard routes
 * and full-page navigation for external or non-dashboard routes.
 */
export const routeToLink = (
  route: string,
  navigate?: (path: string) => void
): void => {
  const toUrl = new URL(route, window.location.origin);

  if (toUrl.origin === window.location.origin) {
    toUrl.pathname = toUrl.pathname.replace(/^\/mobile/, "");
    if (isRouteInDashboard(toUrl.pathname)) {
      if (navigate) {
        navigate(toUrl.pathname);
      } else {
        window.location.assign(toUrl.pathname);
      }
      return;
    }
  }
  window.location.assign(toUrl.toString());
};

export const handlePromptNavigation = (
  prompt: OnboardingPrompt,
  options: {
    navigate?: (path: string) => void;
    eventHandlerMap?: OnboardingEventCallbackMap;
  } = {}
): void => {
  const { link, event_name: eventName } = prompt;
  const { navigate, eventHandlerMap } = options;

  if (link) {
    routeToLink(link, navigate);
  } else if (eventName && eventHandlerMap?.[eventName]) {
    eventHandlerMap[eventName]?.();
  }
};

/**
 * Builds the shared event handler map for onboarding prompts and
 * provides the dialog components that event-based prompts may open.
 *
 * Callers must render `renderDialogs` in their JSX tree so that
 * event-triggered dialogs (e.g. income reporting) are mounted.
 */
export const useOnboardingEventHandlers = (
  options: { onSuccess?: () => void } = {}
): {
  eventHandlerMap: OnboardingEventCallbackMap;
  renderDialogs: ReactNode;
} => {
  const { onSuccess } = options;
  const incomeReporting = useIncomeReportingCard();

  const eventHandlerMap: OnboardingEventCallbackMap = useMemo(
    () => ({
      [OnboardingEventName.INCOME_REPORTING]: incomeReporting.openFormHandler,
    }),
    [incomeReporting.openFormHandler]
  );

  const renderDialogs = useMemo(
    () => (
      <IncomeReportingContainer
        DialogFormProps={{
          isOpen: incomeReporting.isFormOpen,
          onSuccess,
          onUserDismiss: incomeReporting.dismissFormHandler,
        }}
      />
    ),
    [incomeReporting.isFormOpen, onSuccess, incomeReporting.dismissFormHandler]
  );

  return { eventHandlerMap, renderDialogs };
};
